import { useState, useEffect } from 'react';
import { assessmentAPI, ApiResult } from '../../../../services/api';
import { usePermissions } from '../../../../hooks/usePermissions';
import { PERMISSIONS } from '../../../../utils/permissions';
import { useAuth } from '../../../../contexts/AuthContext';
import { useClient } from '../../../../contexts/ClientContext';
import { filterQuestionnaireByAnswers, filterQuestionnaireByPersona, Section, Question, Answers } from '../utils/conditionalLogic';

type CurrentStep = 'assessments' | 'persona-selection' | 'questionnaire' | 'processing' | 'results' | 'incomplete-warning' | 'missing-sections';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface AssessmentResultsData {
  id?: string;
  assessment_id?: string;
  overallScore?: number | null;
  overall_score?: number | null;
  score?: number | null;
  readinessLevel?: string | null;
  readiness_level?: string | null;
  categoryScores?: Record<string, number>;
  category_scores?: Record<string, number>;
  categories?: Record<string, number>;
  roadmap?: Array<{ phase: string; items?: string[] }>;
  implementationRoadmap?: Array<{ phase: string; items?: string[] }>;
  awsServices?: Array<Record<string, any>>;
  aws_services?: Array<Record<string, any>>;
  azureServices?: Array<Record<string, any>>;
  azure_services?: Array<Record<string, any>>;
  recommendations?: Array<Record<string, any>>;
  scorecard?: Array<Record<string, any>>;
  status?: string;
  [key: string]: unknown;
}

export interface AssessmentListItem {
  id: string;
  status: string;
  score?: number | null;
  is_draft?: boolean;
  created_at: string;
  client_id?: string;
  [key: string]: unknown;
}

interface UseAssessmentOptions {
  cloudProvider?: string;
}

interface UseAssessmentReturn {
  questionnaire: Section[] | null;
  fullQuestionnaire: Section[] | null;
  currentStep: CurrentStep;
  setCurrentStep: React.Dispatch<React.SetStateAction<CurrentStep>>;
  currentSectionIndex: number;
  setCurrentSectionIndex: React.Dispatch<React.SetStateAction<number>>;
  currentSubsectionIndex: number;
  setCurrentSubsectionIndex: React.Dispatch<React.SetStateAction<number>>;
  answers: Answers;
  setAnswers: React.Dispatch<React.SetStateAction<Answers>>;
  assessmentResults: AssessmentResultsData | null;
  setAssessmentResults: React.Dispatch<React.SetStateAction<AssessmentResultsData | null>>;
  loading: boolean;
  resultsLoading: boolean;
  submittedAssessmentId: string | null;
  setSubmittedAssessmentId: React.Dispatch<React.SetStateAction<string | null>>;
  assessmentsList: AssessmentListItem[];
  assessmentsLoading: boolean;
  initialLoading: boolean;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  loadQuestionnaire: () => Promise<void>;
  loadAssessments: () => Promise<void>;
  calculateAssessment: () => Promise<{ success: boolean; error: string } | void>;
  checkAssessmentResults: (assessmentId: string) => Promise<void>;
  submitAssessment: (assessmentIdFromUrl?: string | null) => Promise<ApiResult>;
  createDraftAssessment: () => Promise<ApiResult & { prefillAnswers?: Answers }>;
  userPersona: string | null;
  handleSetPersona: (persona: string) => void;
}

/** Collect all questions from a nested questionnaire structure. */
const getAllQuestions = (questionnaire: Section[] | null): Question[] => {
  const questions: Question[] = [];
  (questionnaire || []).forEach(section => {
    (section.sections || []).forEach(sub => {
      questions.push(...(sub.questions || []));
    });
  });
  return questions;
};

/** Check if a questionnaire contains a question with the given ID. */
const hasQuestion = (questionnaire: Section[] | null, questionId: string): boolean =>
  getAllQuestions(questionnaire).some(q => q.id === questionId);

/** Remove a question by ID from a nested questionnaire structure. */
const removeQuestion = (questionnaire: Section[] | null, questionId: string): Section[] =>
  (questionnaire || []).map(section => ({
    ...section,
    sections: (section.sections || []).map(sub => ({
      ...sub,
      questions: (sub.questions || []).filter(q => q.id !== questionId)
    }))
  }));

export const useAssessment = ({ cloudProvider }: UseAssessmentOptions = {}): UseAssessmentReturn => {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const canActOnClients =
    hasPermission(PERMISSIONS.CLIENTS_VIEW) ||
    hasPermission(PERMISSIONS.CLIENTS_MANAGE) ||
    hasPermission(PERMISSIONS.PARTNERS_VIEW) ||
    hasPermission(PERMISSIONS.PARTNERS_MANAGE);
  const { selectedClientId } = useClient();
  const [fullQuestionnaire, setFullQuestionnaire] = useState<Section[] | null>(null);
  const [questionnaire, setQuestionnaire] = useState<Section[] | null>(null);
  const [currentStep, setCurrentStep] = useState<CurrentStep>('assessments');
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [currentSubsectionIndex, setCurrentSubsectionIndex] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [assessmentResults, setAssessmentResults] = useState<AssessmentResultsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [submittedAssessmentId, setSubmittedAssessmentId] = useState<string | null>(null);
  const [assessmentsList, setAssessmentsList] = useState<AssessmentListItem[]>([]);
  const [assessmentsLoading, setAssessmentsLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userPersona, setUserPersona] = useState<string | null>(null);

  // Helper: apply full filtering pipeline (answers + persona)
  const applyFiltering = (sections: Section[], currentAnswers: Answers, persona: string | null): Section[] => {
    const afterAnswers = filterQuestionnaireByAnswers(sections, currentAnswers);
    return filterQuestionnaireByPersona(afterAnswers, persona);
  };

  // Set persona and persist it in answers
  const handleSetPersona = (persona: string) => {
    setUserPersona(persona);
    setAnswers(prev => ({ ...prev, user_persona: persona }));
  };

  // When cloud_provider is already set in answers (from draft or onboarding),
  // filter out the cloud_provider question from the questionnaire
  useEffect(() => {
    if (!fullQuestionnaire?.length) return;
    if (!answers.cloud_provider) return;

    // Check if any section still has the cloud_provider question
    if (hasQuestion(fullQuestionnaire, 'cloud_provider')) {
      const filtered = removeQuestion(fullQuestionnaire, 'cloud_provider');
      setFullQuestionnaire(filtered);
      // Also update the displayed questionnaire immediately
      setQuestionnaire(applyFiltering(filtered, answers, userPersona));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers.cloud_provider, fullQuestionnaire]);

  // Re-filter questionnaire when adaptive question answers change
  const aiImplementationStage = answers['ai_implementation_stage'];
  const dataPipelinesCount = answers['data_pipelines_count'];
  const aiMlUseCases = answers['ai_ml_use_cases'];
  const aiPocsCount = answers['ai_pocs_count'];
  const aiProductionWorkflows = answers['ai_production_workflows_count'];

  useEffect(() => {
    if (!fullQuestionnaire?.length) return;

    const adaptiveQuestions = [
      'ai_implementation_stage',
      'data_pipelines_count',
      'ai_ml_use_cases',
      'ai_pocs_count',
      'ai_production_workflows_count'
    ];

    // Check if any adaptive question was answered
    const hasAdaptiveAnswers = adaptiveQuestions.some(q => answers[q] !== undefined);

    if (hasAdaptiveAnswers) {
      const filteredSections = applyFiltering(fullQuestionnaire, answers, userPersona);

      // Only update if the structure actually changed
      const newStructure = JSON.stringify(filteredSections.map(s => ({ id: s.id, subs: s.sections.map(ss => ss.id) })));
      const currentStructure = JSON.stringify((questionnaire || []).map(s => ({ id: s.id, subs: s.sections.map(ss => ss.id) })));

      if (newStructure !== currentStructure) {
        setQuestionnaire(filteredSections);

        // Adjust current section index if needed
        if (currentSectionIndex >= filteredSections.length) {
          setCurrentSectionIndex(Math.max(0, filteredSections.length - 1));
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiImplementationStage, dataPipelinesCount, aiMlUseCases, aiPocsCount, aiProductionWorkflows, fullQuestionnaire]);

  // Re-filter when persona changes
  useEffect(() => {
    if (!fullQuestionnaire?.length) return;
    const filteredSections = applyFiltering(fullQuestionnaire, answers, userPersona);
    setQuestionnaire(filteredSections);

    // Adjust current section index if needed
    if (currentSectionIndex >= filteredSections.length) {
      setCurrentSectionIndex(Math.max(0, filteredSections.length - 1));
      setCurrentSubsectionIndex(0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPersona]);

  const loadQuestionnaire = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch('/questionnaire.json');
      const data = await response.json();
      const sections = data.sections || [];

      // If cloud provider is pre-set (onboarding mode), seed the answer immediately
      if (cloudProvider) {
        setAnswers(prev => ({ ...prev, cloud_provider: cloudProvider }));
      }

      // Store full questionnaire and apply initial filtering
      // (cloud_provider question removal is handled reactively by the effect above)
      setFullQuestionnaire(sections);
      const currentAnswers = cloudProvider ? { ...answers, cloud_provider: cloudProvider } : answers;
      const filteredSections = applyFiltering(sections, currentAnswers, userPersona);
      setQuestionnaire(filteredSections);
    } catch (err: unknown) {
      console.error('Error loading questionnaire:', err);
      // Fallback to empty questionnaire structure
      setFullQuestionnaire([]);
      setQuestionnaire([]);
    } finally {
      setLoading(false);
    }
  };

  const loadAssessments = async () => {
    setAssessmentsLoading(true);
    try {
      const result = await assessmentAPI.getAssessments(user?.role as string, (user as unknown as Record<string, unknown>)?.tenant_id as string | null ?? null, selectedClientId);
      if (result.success) {
        setAssessmentsList(result.data);
      } else {
        console.error('Failed to load assessments:', result.error);
        setError(result.error || 'Failed to load assessments');
      }
    } catch (err: unknown) {
      console.error('Error loading assessments:', err);
      setError('Failed to connect to the server. Please try again later.');
    } finally {
      setAssessmentsLoading(false);
    }
  };

  const calculateAssessment = async () => {
    setLoading(true);
    try {
      // For partners, include the selected client_id when creating assessments on behalf of clients
      const clientId = canActOnClients ? selectedClientId : (user?.client_id || null);


      // Validate that partners have a client selected
      if (canActOnClients && !clientId) {
        const errorMsg = 'Please select a client before creating an assessment';
        setError(errorMsg);
        return { success: false, error: errorMsg };
      }

      const result = await assessmentAPI.calculateAssessment(answers, clientId);
      if (result.success) {
        const assessmentId = result.data.id;
        setSubmittedAssessmentId(assessmentId);

        // Reload assessments list to show the new assessment
        await loadAssessments();

        // Check if results are ready - must have meaningful data
        const hasScore = result.data.score != null;
        const hasOverallScore = result.data.overallScore != null;
        const hasCategoryScores = result.data.categoryScores && Object.keys(result.data.categoryScores).length > 0;
        const hasRoadmap = (result.data.roadmap?.length ?? 0) > 0;
        const isProcessing = result.data.status === 'processing';

        // Results are only ready if we have score/overallScore AND (category scores OR roadmap)
        const resultsReady = !isProcessing && (hasScore || hasOverallScore) && (hasCategoryScores || hasRoadmap);

        if (resultsReady) {
          // Results are ready - transform and display them
          const transformedResults = transformAssessmentResults(result.data);
          setAssessmentResults(transformedResults);
          setCurrentStep('results');
        } else {
          // Results not ready yet - go to processing screen
          setCurrentStep('processing');
        }
      } else {
        setError(result.error || 'Failed to calculate assessment');
      }
    } catch (err: unknown) {
      console.error('Error calculating assessment:', err);
      setError('Failed to calculate assessment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Transform API response to match component expectations
  const transformAssessmentResults = (data: AssessmentResultsData): AssessmentResultsData => {
    // Map snake_case API fields to camelCase component fields (matching the reference component)
    const transformed = {
      id: data.id,
      // Map overall_score to overallScore
      overallScore: data.overallScore ?? data.overall_score ?? data.score ?? null,
      // Map readiness_level to readinessLevel
      readinessLevel: data.readinessLevel || data.readiness_level || null,
      // Map category_scores to categoryScores
      categoryScores: data.categoryScores || data.category_scores || data.categories || {},
      // Map roadmap to roadmap (keeping the same name as reference component uses 'roadmap')
      roadmap: data.roadmap || data.implementationRoadmap || [],
      // Keep aws_services as-is (reference component uses awsServices with service/useCase structure)
      awsServices: data.awsServices || data.aws_services || [],
      azureServices: data.azureServices || data.azure_services || [],
      // Include recommendations if present
      recommendations: data.recommendations || [],
      // Include scorecard if present
      scorecard: data.scorecard || [],
      // Keep original data for reference
      ...data
    };

    return transformed;
  };

  const checkAssessmentResults = async (assessmentId: string) => {
    setResultsLoading(true);
    try {
      const result = await assessmentAPI.getAssessmentResults(assessmentId);

      if (result.success) {
        // Check if results are ready based on backend status
        const hasScore = result.data.score != null;
        const hasOverallScore = result.data.overallScore != null;
        const isProcessing = result.data.status === 'processing';


        // Results are ready if backend returns data (not processing status) and we have a score
        // Trust the backend: if it returns AssessmentResult data, the assessment is complete
        // Check both camelCase and snake_case for score fields
        const hasAnyScore = hasScore || hasOverallScore ||
          result.data.overall_score != null;
        const resultsReady = !isProcessing && hasAnyScore;


        if (resultsReady) {
          // Results are ready - transform the response
          const transformedResults = transformAssessmentResults(result.data);
          setAssessmentResults(transformedResults);
          setCurrentStep('results');
        } else {
          // Results not ready yet - stay on processing screen
          setCurrentStep('processing');
          setSubmittedAssessmentId(assessmentId);
        }
      } else {
        console.error('Failed to get assessment results:', result.error);
        setCurrentStep('processing');
      }
    } catch (err: unknown) {
      console.error('Error checking assessment results:', err);
      setCurrentStep('processing');
    } finally {
      setResultsLoading(false);
    }
  };

  const submitAssessment = async (assessmentIdFromUrl: string | null = null): Promise<ApiResult> => {
    setLoading(true);
    try {
      let result;


      // If assessment_id is provided (from onboarding URL), submit the existing draft
      if (assessmentIdFromUrl) {

        result = await assessmentAPI.submitDraftAssessment(assessmentIdFromUrl, answers);
      } else {
        // Otherwise, create a new assessment
        // For partners, include the selected client_id when creating assessments on behalf of clients
        const clientId = canActOnClients ? selectedClientId : (user?.client_id || null);


        // Validate that partners have a client selected
        if (canActOnClients && !clientId) {
          const errorMsg = 'Please select a client before creating an assessment';
          setError(errorMsg);
          return { success: false, error: errorMsg };
        }

        result = await assessmentAPI.submitAssessment(answers, clientId);
      }

      if (result.success) {
        setSubmittedAssessmentId(result.data.id);
        setCurrentStep('processing');
        return result;
      } else {
        // Check if assessment was actually submitted despite the error (false negative)
        // This can happen with timeouts or network issues after backend processed the request
        if (assessmentIdFromUrl) {
          try {
            const draft = await assessmentAPI.loadDraft(assessmentIdFromUrl);
            if (['processing', 'success'].includes(draft.status)) {
              setSubmittedAssessmentId(assessmentIdFromUrl);
              setCurrentStep('processing');
              return { success: true };
            }
          } catch (checkError: unknown) {
            console.error('[useAssessment] Error checking assessment status:', checkError);
            // Ignore check error, fall through to show original error
          }
        }
        setError(result.error || 'Failed to submit assessment');
        return result;
      }
    } catch (err: unknown) {
      console.error('Error submitting assessment:', err);

      // Check if assessment was actually submitted despite the error (false negative)
      // This can happen with timeouts or network issues after backend processed the request
      if (assessmentIdFromUrl) {
        try {
          const draft = await assessmentAPI.loadDraft(assessmentIdFromUrl);
          if (['processing', 'success'].includes(draft.status)) {
            setSubmittedAssessmentId(assessmentIdFromUrl);
            setCurrentStep('processing');
            return { success: true };
          }
        } catch (checkError: unknown) {
          console.error('[useAssessment] Error checking assessment status:', checkError);
          // Ignore check error, fall through to show original error
        }
      }

      setError('Failed to submit assessment. Please try again.');
      return { success: false, error: err instanceof Error ? err.message : String(err) };
    } finally {
      setLoading(false);
    }
  };

  const createDraftAssessment = async (): Promise<ApiResult & { prefillAnswers?: Answers }> => {
    setLoading(true);
    try {
      // For partners, include the selected client_id when creating drafts on behalf of clients
      const clientId = canActOnClients ? selectedClientId : (user?.client_id || null);


      // Validate that partners have a client selected
      if (canActOnClients && !clientId) {
        const errorMsg = 'Please select a client before creating an assessment';
        setError(errorMsg);
        return { success: false, error: errorMsg };
      }

      const result = await assessmentAPI.createDraftAssessment(clientId);
      if (result.success) {

        // Try to pre-fill answers from latest completed assessment
        let prefillAnswers: Answers = {};
        const prefillClientId = result.data.client_id || clientId;
        try {
          const latestResult = await assessmentAPI.getLatestCompletedResponses(prefillClientId);
          if (latestResult.success && latestResult.data?.responses && Object.keys(latestResult.data.responses).length > 0) {
            const prevResponses = { ...latestResult.data.responses };
            // Remove user_persona — new user picks their own
            delete prevResponses.user_persona;
            // Only carry answers for questions that still exist in the current questionnaire
            if (fullQuestionnaire) {
              const validQuestionIds = new Set(getAllQuestions(fullQuestionnaire).map(q => q.id));
              Object.keys(prevResponses).forEach(key => {
                if (!validQuestionIds.has(key)) {
                  delete prevResponses[key];
                }
              });
            }
            prefillAnswers = prevResponses;
          }
        } catch (prefillError: unknown) {
        }

        return { success: true, data: result.data, prefillAnswers };
      } else {
        setError(result.error || 'Failed to create draft assessment');
        return result;
      }
    } catch (err: unknown) {
      console.error('Error creating draft assessment:', err);
      setError('Failed to create draft assessment. Please try again.');
      return { success: false, error: err instanceof Error ? err.message : String(err) };
    } finally {
      setLoading(false);
    }
  };

  const initializeComponent = async () => {
    setInitialLoading(true);
    setError(null);
    try {
      const result = await assessmentAPI.getAssessments(user?.role as string, (user as unknown as Record<string, unknown>)?.tenant_id as string | null ?? null, selectedClientId);
      if (result.success) {
        setAssessmentsList(result.data);
      } else {
        console.error('Failed to load assessments:', result.error);
        setError(result.error || 'Failed to load assessments');
        setCurrentStep('questionnaire');
      }
    } catch (err: unknown) {
      console.error('Error loading assessments:', err);
      setError('Failed to connect to the server. Please try again later.');
      setCurrentStep('questionnaire');
    } finally {
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      initializeComponent();
    } else {
      setInitialLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, selectedClientId]);

  useEffect(() => {
    loadQuestionnaire();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    questionnaire,
    fullQuestionnaire,
    currentStep,
    setCurrentStep,
    currentSectionIndex,
    setCurrentSectionIndex,
    currentSubsectionIndex,
    setCurrentSubsectionIndex,
    answers,
    setAnswers,
    assessmentResults,
    setAssessmentResults,
    loading,
    resultsLoading,
    submittedAssessmentId,
    setSubmittedAssessmentId,
    assessmentsList,
    assessmentsLoading,
    initialLoading,
    error,
    setError,
    loadQuestionnaire,
    loadAssessments,
    calculateAssessment,
    checkAssessmentResults,
    submitAssessment,
    createDraftAssessment,
    userPersona,
    handleSetPersona,
  };
};

export default useAssessment;
